import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { usePrevious } from '@react-hooks-library/core';

import { useUserApiMutation } from "src/lib/filmwebid/useUserApiMutation";
import { useUserContext } from "src/lib/contexts/UserContext";
import { useQueryClient } from "@tanstack/react-query";
import { buildUserApiQueryKey } from "src/lib/filmwebid/useUserApiQuery";
import { IMovieActivity, IUserDataForMovie } from "src/lib/filmwebid/IMovieActivity";

type UseWatchListArgs = {
	edi?: string | null;
	streamingId?: number | null;
	onComplete?: () => void;
	initialValue?: boolean | null;
}

export default function useWatchlist({ edi, streamingId, onComplete, initialValue }: UseWatchListArgs): [(value: boolean | null) => void, boolean] {

	const { updateWatchlistIdList } = useUserContext();

	const queryClient = useQueryClient();

	const { mutate, isPending } = useUserApiMutation("/api/Watchlist", { allowAnon: false }, {
		onMutate: async (variables: { method: string }) => {
			debugger;
			const queryKey = buildUserApiQueryKey("/api/Movie/MovieActivity", { edi, streamingId })
			// Cancel any outgoing refetches
			// (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot the previous value
			const previousData = queryClient.getQueryData<IMovieActivity>(queryKey)

			const movieUserInteraction: IUserDataForMovie = previousData?.movieUserInteraction ?? {
				watchlisted: false,
				favourite: false,
				review: "",
				reportedReviews: [],
				reviewId: undefined,
				rating: undefined,
			};
			// Optimistically update to the new value
			queryClient.setQueryData<IMovieActivity>(queryKey, (old) => ({
				...old, 
				movieUserInteraction: {
					...movieUserInteraction,
					watchlisted: variables.method === "POST" ? true : false,
				}
			}))

			// Return a context object with the snapshotted value
			return { previousData }
		},
		onSuccess: onComplete
	});
	const setWatchlist = useCallback((value: boolean | null) => {
		updateWatchlistIdList({ edi, streamingId }, !!value);
		if (value) {
			mutate({
				method: "POST",
				body: { EDI: edi ?? null, streamingId: streamingId ?? null, source: window?.location?.hostname ?? null }
			});
		} else {
			mutate({
				method: "DELETE",
				body: { edi: edi ?? null, streamingId: streamingId ?? null }
			});
		}
	}, [edi, mutate, streamingId, updateWatchlistIdList]);

	return [setWatchlist, isPending];
}